/**
 * Triggers a browser download for arbitrary file content.
 *
 * Raw content is converted to a Blob unless the caller already provides a Blob.
 * The resulting Blob is exposed through a temporary object URL and assigned to an
 * anchor element with the given file name. The object URL is revoked after the
 * click to avoid keeping the in-memory resource alive.
 *
 * @param data - File content to download. Can be a prebuilt Blob or any value
 * accepted by the Blob constructor, such as a string, ArrayBuffer, or Uint8Array.
 * @param fileName - Name that should be used for the downloaded file in the browser.
 * @returns Nothing. The function performs the download as a browser side effect.
 */
export const downloadFile = (data: BlobPart | Blob, fileName: string): void => {
  const blob = data instanceof Blob ? data : new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Triggers a CSV download using UTF-8 encoding with a byte order mark (BOM).
 *
 * The BOM is prepended so spreadsheet applications such as Microsoft Excel detect
 * the file as UTF-8 and display non-ASCII characters like umlauts (ö,ä,ü)
 * correctly. This helper creates a CSV Blob with the correct MIME type and then
 * delegates the actual browser download handling to {@link downloadFile}.
 *
 * @param csvContent - Plain CSV text content, including the header row and line
 * separators between records.
 * @param fileName - Name that should be used for the downloaded CSV file.
 * @returns Nothing. The function performs the download as a browser side effect.
 */
export const downloadCSVFile = (csvContent: string, fileName: string) => {
  const blob = new Blob(['\uFEFF', csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  downloadFile(blob, fileName);
};
